
<template>
    <b-container fluid>
        <b-row class="mb-4">
            <h1 class="text-left">{{ $t('dashboard.statistieken.title') }}</h1>
        </b-row>
        <b-row class="mb-2 d-flex justify-content-start">
            <base-button
                class="float-right"
                variant="primary"
                @click="downloadActive = true"
                :text="$t('dashboard.statistieken.download')"
            />
            <b-modal
                :visible="downloadActive"
                no-close-on-esc
                no-close-on-backdrop
                no-fade
                hide-footer
                hide-header
                modal-class="exportStatsModal"
            >
                <div class="panel-header">
                    <h3 class="panel-header-title">
                        {{ $t('dashboard.statistieken.download') }}
                    </h3>
                    <!-- Close icon -->
                    <icon-close
                        @close="downloadActive = false"
                        class="d-inline-block float-right"
                    />
                </div>
                <b-container>
                    <usage-download :showHeader="false" />
                </b-container>
            </b-modal>
        </b-row>
        <br />
        <h6 class="text-left">{{ $t('dashboard.statistieken.datefrom') }}</h6>
        <b-row>
            <b-col cols="3">
                <b-form-datepicker
                    id="start-datepicker"
                    v-model="start"
                    value-as-date
                    class="mb-2"
                    :locale="getLocale"
                    :min="minFrom"
                    :max="end"
                ></b-form-datepicker>
            </b-col>
        </b-row>
        <h6 class="text-left">{{ $t('dashboard.statistieken.dateto') }}</h6>
        <b-row>
            <b-col cols="3">
                <b-form-datepicker
                    id="end-datepicker"
                    v-model="end"
                    value-as-date
                    class="mb-2"
                    :locale="getLocale"
                    :min="start"
                    :max="maxTo"
                ></b-form-datepicker>
            </b-col>
        </b-row>
        <b-form-select
            v-if="showProfilesSelection"
            :value="selectedProfile"
            @change="selectedProfile = $event"
            :options="themeProfiles"
        />
        <br />
        <b-row v-for="chart in charts" :key="chart.name">
            <h2>{{ chart.name }}</h2>
            <div
                :is="chart.component || 'usage-chart-group'"
                :organizationId="getOrganizationId"
                :themeProfileId="
                    filterUserProfileIds(chart.settings.omitProfileSelection)
                "
                :chartConfig="chart"
                :useChart="showDataAsChart"
                :start="start"
                :end="end"
            />
        </b-row>
    </b-container>
</template>
<script>
    import { mapActions, mapGetters } from 'vuex';
    import UsageChartGroup from './UsageChartGroup.vue';
    import ThemesUsageChart from './ThemesUsageChart.vue';
    import UsageOverview from './UsageOverview.vue';
    import UsageDownload from './download/index.vue';
    import BaseButton from '../../BaseComponents/base-button.vue';
    import IconClose from '../../IconClose.vue';

    import { PROFILE_TYPE } from '../../../constants';
    export default {
        components: {
            'usage-chart-group': UsageChartGroup,
            'usage-overview': UsageOverview,
            'usage-download': UsageDownload,
            'themes-usage-chart': ThemesUsageChart,
            'base-button': BaseButton,
            'icon-close': IconClose
        },

        data: function () {
            return {
                showProfilesSelection: false,
                charts: null,
                themeProfiles: null,
                selectedProfile: null,
                showDataAsChart: true,
                downloadActive: false,

                start: null,
                end: null,
                minFrom: null,
                maxTo: null
            };
        },

        mounted: function () {
            this.init();
        },

        created() {
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 7);
            this.start = startDate;

            const endDate = new Date();
            this.end = endDate;
            this.maxTo = endDate;

            const minDate = new Date();
            // startdate is two years ago and First January of that year
            minDate.setFullYear(minDate.getFullYear() - 2);
            minDate.setMonth(0);
            minDate.setDate(1);
            this.minFrom = minDate;
        },

        computed: {
            ...mapGetters(['getLocale']),
            getOrganizationId() {
                return this.$store.state.userInfo.organizationId;
            }
        },

        methods: {
            ...mapActions(['useApiData']),

            async init() {
                await this.getCharts();
                this.getProfiles();
            },

            filterUserProfileIds(omitprofiles) {
                return !omitprofiles ? this.selectedProfile : null;
            },

            isObject(value) {
                return !!(
                    value &&
                    typeof value === 'object' &&
                    !Array.isArray(value)
                );
            },

            findObject(
                object = {},
                objectKeyToMatch = '',
                objectFieldToMatch = ''
            ) {
                if (!this.isObject(object)) {
                    return null;
                }

                const parentEntries = Object.entries(object);

                for (const parentEntry of parentEntries) {
                    const [objectKey, objectValue] = parentEntry;

                    const matchedValue = this.matchObjectValue(
                        objectKey,
                        objectValue,
                        objectKeyToMatch,
                        objectFieldToMatch
                    );
                    if (matchedValue) {
                        return matchedValue;
                    }

                    if (this.isObject(objectValue)) {
                        const matchedChildValue = this.findObject(
                            objectValue,
                            objectKeyToMatch,
                            objectFieldToMatch
                        );

                        if (matchedChildValue !== null) {
                            return matchedChildValue;
                        }
                    }
                }
                return null;
            },

            matchObjectValue(
                objectKey,
                objectValue,
                objectKeyToMatch,
                objectFieldToMatch
            ) {
                if (objectKey === objectKeyToMatch && this.isObject(objectValue)) {
                    const childEntries = Object.entries(objectValue);

                    for (const childEntry of childEntries) {
                        const [childObjectKey] = childEntry;

                        if (childObjectKey === objectFieldToMatch) {
                            return objectValue;
                        }
                    }
                }
                return null;
            },

            async getCharts() {
                this.charts = [];

                const logQueriesResult = await this.useApiData({
                    name: 'GetAnalyticsArchiveConfiguration',
                    data: { organizationId: this.getOrganizationId }
                });
                for (const query of logQueriesResult.data) {
                    const chart = {
                        name: this.$t(query.name),
                        component: 'usage-overview',
                        chartType: 'ComboChart',
                        filter: query.filter,
                        requestType: query.queryType,
                        settings: JSON.parse(query.settings)
                    };

                    const hAxis = this.findObject(chart.settings, 'hAxis', 'title');
                    if (hAxis) {
                        hAxis.title = this.$t(hAxis.title);
                    }
                    const vAxis = this.findObject(chart.settings, 'vAxis', 'title');
                    if (vAxis) {
                        vAxis.title = this.$t(vAxis.title);
                    }

                    const data = this.findObject(
                        chart.settings,
                        'data',
                        'formatter'
                    );
                    if (data) {
                        if (data.accumulator && data.accumulator === 'duration') {
                            // duration in minutes
                            data.formatter = (v) => Math.ceil(v / 1000 / 60);
                        }
                    }

                    this.charts.push(chart);
                }
            },

            async getProfiles() {
                if (!this.showProfilesSelection) {
                    return;
                }
                const profilesResult = await this.$store.dispatch('useApiData', {
                    name: 'GetProfileOverview',
                    parameters: { profileTypeId: PROFILE_TYPE.THEME_FEEDBACK }
                });
                if (profilesResult?.data) {
                    this.themeProfiles = [
                        {
                            value: null,
                            text: this.$t('dashboard.statistieken.showall')
                        },
                        ...profilesResult.data.map((item) => {
                            return { value: item.id, text: item.name };
                        })
                    ];
                } else {
                    this.themeProfiles = [];
                }
            }
        }
    };
</script>

<style lang="scss">
.exportStatsModal .modal-dialog {
    position: absolute;
    margin: 0px;
    top: 15%;
    left: calc(50% - 215px);
    min-height: 40px;
    min-width: 540px;
    max-width: 540px;

    & .modal-content {
        height: 100%;
        border: none;
        border-radius: initial;
    }
}

.exportStatsModal .modal-body {
    padding: 0px;
    height: 100%;
}

@media (max-width: 767px) {
    .exportStatsModal .modal-dialog {
        top: 0 !important;
        left: 0%;
        bottom: 0 !important;
        right: 0;
        min-width: 0;
        max-width: none;
    }
}
</style>


<style lang="scss" scoped>
h2 {
    width: 100%;
}
</style>
